const STATUS_VALUES = new Set(["open", "closed", "all"] as const);
const MODE_VALUES = new Set([
  "human_queue",
  "pending_human",
  "human",
  "bot",
  "all",
] as const);
const TAG_VALUES = new Set([
  "partnership",
  "support",
  "spam",
  "unknown",
] as const);

export type InboxStatusFilter = "open" | "closed" | "all";
export type InboxModeFilter =
  | "human_queue"
  | "pending_human"
  | "human"
  | "bot"
  | "all";
export type InboxTagFilter = "partnership" | "support" | "spam" | "unknown";

export interface InboxSearch {
  status: InboxStatusFilter;
  mode: InboxModeFilter;
  search: string;
  tags: InboxTagFilter[];
  page: number;
  conversation?: string;
}

const DEFAULT_SEARCH: InboxSearch = {
  status: "open",
  mode: "human_queue",
  search: "",
  tags: [],
  page: 1,
};

const parseTags = (raw: unknown): InboxTagFilter[] => {
  if (Array.isArray(raw)) {
    return raw
      .map((tag) => String(tag).trim())
      .filter((tag): tag is InboxTagFilter =>
        TAG_VALUES.has(tag as InboxTagFilter)
      );
  }
  if (typeof raw === "string" && raw.length > 0) {
    return raw
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag): tag is InboxTagFilter =>
        TAG_VALUES.has(tag as InboxTagFilter)
      );
  }
  return [];
};

export function validateInboxSearch(
  search: Record<string, unknown>,
): InboxSearch {
  const status = typeof search.status === "string" &&
      STATUS_VALUES.has(search.status as InboxStatusFilter)
    ? (search.status as InboxStatusFilter)
    : DEFAULT_SEARCH.status;

  const mode = typeof search.mode === "string" &&
      MODE_VALUES.has(search.mode as InboxModeFilter)
    ? (search.mode as InboxModeFilter)
    : DEFAULT_SEARCH.mode;

  const searchTerm = typeof search.search === "string"
    ? search.search.trim()
    : "";

  const tags = parseTags(search.tags);

  const pageRaw = Number(search.page);
  const page = Number.isFinite(pageRaw) && pageRaw > 0
    ? Math.floor(pageRaw)
    : DEFAULT_SEARCH.page;

  const conversation = typeof search.conversation === "string" &&
      /^[A-Za-z0-9_-]{1,128}$/.test(search.conversation.trim())
    ? search.conversation.trim()
    : undefined;

  return { status, mode, search: searchTerm, tags, page, conversation };
}

export const inboxSearchDefaults = DEFAULT_SEARCH;
